import axios from "axios";
import {
  ACTIVATE_PROJECT,
  CREATE_SUB_PROGRAM_URL,
  DEACTIVATE_PROJECT,
  DELETE_SUB_PROGRAM_URL,
  EDIT_PROJECT,
  GET_DEACTIVATED_PROJECT,
  LIST_SUB_PROGRAMS_URL,
  UPDATE_SUB_PROGRAM_URL,
} from "@/utils/constants/constants.js";
import { validate_non_empty_string, validate_non_sub_zero_number } from "@/utils/validations/validate_inputs";

function parse_response_data(response_data) {
  return typeof response_data === "string" ? JSON.parse(response_data) : response_data;
}

function build_project_update_state(project) {
  return {
    project_id: project.id,
    update_company_name: project.name || "",
    update_company_description: project.project_description || "",
    update_selected_company_type: project.project_type?.id || "",
  };
}

export default {
  methods: {
    deactivate_company(project_id) {
      this.show_confirm("Deactivate Programme", "Are you sure you want to deactivate this programme?", () => {
        return axios.post(DEACTIVATE_PROJECT, { project_id }, { headers: this.headers }).then((response) => {
          const response_data = parse_response_data(response.data);
          if (response_data.status === "success") {
            this.show_success("Programme successfully deactivated");
            window.location.reload();
            return;
          }
          this.show_error(response_data.message || "Failed to deactivate programme");
        }).catch((error) => {
          this.show_error(`Error deactivating programme: ${error}`);
        });
      });
    },
    get_deactivated_projects() {
      return axios.get(GET_DEACTIVATED_PROJECT, { headers: this.headers }).then((response) => {
        const parsed_response = parse_response_data(response.data);
        if (parsed_response.status === "success") {
          this.deactivated_projects = parsed_response.data;
          this.$nextTick(() => this.initialize_data_table());
          return;
        }
        this.show_error(parsed_response.message || "No deactivated programme found");
      }).catch((error) => {
        this.show_error(`Error fetching deactivated programmes: ${error}`);
      });
    },
    activate_projects(project_id) {
      this.show_confirm("Activate Programme", "Are you sure you want to activate this programme?", () => this.handle_project_activation(project_id));
    },
    handle_project_activation(project_id) {
      return axios.post(ACTIVATE_PROJECT, { project_id }, { headers: this.headers }).then(async (response) => {
        const response_data = parse_response_data(response.data);
        if (response_data.status !== "success") {
          this.show_error(response_data.message || "Failed to activate programme");
          return;
        }
        this.show_success("Programme successfully activated");
        await this.get_companies(false);
        await this.get_deactivated_projects();
      }).catch((error) => {
        const message = error.response?.data?.message || error.message || error;
        this.show_error(`Error activating programme: ${message}`);
      });
    },
    prepopulate_update_company_modal(project_id) {
      const project = this.company_list.find((company) => company.id === project_id);
      if (!project) return;
      Object.assign(this, build_project_update_state(project));
    },
    update_project() {
      if (!validate_non_empty_string(this.update_company_name)) {
        this.show_error("Programme name must not be empty");
        return;
      }
      if (!validate_non_empty_string(this.update_company_description)) {
        this.show_error("Programme description must not be empty");
        return;
      }
      if (!validate_non_sub_zero_number(this.update_selected_company_type)) {
        this.show_error("Please select a valid programme type");
        return;
      }
      return axios.post(EDIT_PROJECT, {
        project_id: this.project_id,
        project_name: this.update_company_name,
        project_description: this.update_company_description,
        project_type_id: this.update_selected_company_type,
      }, { headers: this.headers }).then((response) => {
        const response_data = parse_response_data(response.data);
        if (response_data.status !== "success") {
          this.show_error(response_data.message || "Failed to update programme");
          return;
        }
        this.show_success("Programme updated successfully");
        this.close_modal("update_company");
        window.location.reload();
      }).catch((error) => {
        this.show_error(`Error updating programme: ${error}`);
      });
    },
    toggle_program_accordion(project_id) {
      const is_expanded = !Reflect.get(this.expanded_programs, project_id);
      this.expanded_programs = { ...this.expanded_programs, [project_id]: is_expanded };
      if (is_expanded && !Reflect.get(this.sub_programs, project_id)) {
        this.fetch_sub_programs(project_id);
      }
    },
    fetch_sub_programs(project_id) {
      return axios.get(LIST_SUB_PROGRAMS_URL, {
        headers: this.headers,
        params: { project_id },
      }).then((response) => {
        const next_sub_programs = response.data.status === "success" ? (response.data.data || []) : [];
        this.sub_programs = { ...this.sub_programs, [project_id]: next_sub_programs };
      }).catch(() => {
        this.sub_programs = { ...this.sub_programs, [project_id]: [] };
      });
    },
    open_create_sub_program(project_id) {
      this.create_sp_project_id = project_id;
      this.create_sp_name = "";
      this.create_sp_description = "";
    },
    create_sub_program() {
      const name = (this.create_sp_name || "").trim();
      if (!name) {
        this.show_error("Please enter a sub-programme name.");
        return Promise.resolve();
      }
      return axios.post(CREATE_SUB_PROGRAM_URL, {
        name,
        description: (this.create_sp_description || "").trim(),
        project_id: this.create_sp_project_id,
      }, { headers: this.headers, withCredentials: true }).then(async (response) => {
        if (response.data.status !== "success") {
          this.show_error(response.data.message || "Failed to create sub-programme.");
          return;
        }
        this.show_success("Sub-programme created successfully.");
        this.create_sp_name = "";
        this.create_sp_description = "";
        this.close_modal("create_sub_program_modal");
        await this.fetch_sub_programs(this.create_sp_project_id);
      }).catch((err) => {
        this.show_error(err?.response?.data?.message || "Failed to create sub-programme.");
      });
    },
    open_edit_sub_program(sp, project_id) {
      this.edit_sp_id = sp.id;
      this.edit_sp_name = sp.name;
      this.edit_sp_description = sp.description || "";
      this.edit_sp_project_id = project_id;
    },
    save_edit_sub_program() {
      const name = (this.edit_sp_name || "").trim();
      if (!name) {
        this.show_error("Sub-programme name cannot be empty.");
        return Promise.resolve();
      }
      return axios.post(UPDATE_SUB_PROGRAM_URL, {
        sub_program_id: this.edit_sp_id,
        name,
        description: (this.edit_sp_description || "").trim(),
      }, { headers: this.headers, withCredentials: true }).then(async (response) => {
        if (response.data.status !== "success") {
          this.show_error(response.data.message || "Failed to update sub-programme.");
          return;
        }
        this.show_success("Sub-programme updated.");
        this.close_modal("edit_sub_program_modal");
        await this.fetch_sub_programs(this.edit_sp_project_id);
      }).catch((err) => {
        this.show_error(err?.response?.data?.message || "Failed to update sub-programme.");
      });
    },
    delete_sub_program(sp_id, project_id) {
      if (!confirm("Are you sure you want to delete this sub-programme?")) {
        return Promise.resolve();
      }
      return axios.post(DELETE_SUB_PROGRAM_URL, {
        sub_program_id: sp_id,
      }, { headers: this.headers, withCredentials: true }).then(async (response) => {
        if (response.data.status !== "success") {
          this.show_error(response.data.message || "Failed to delete sub-programme.");
          return;
        }
        this.show_success("Sub-programme deleted.");
        await this.fetch_sub_programs(project_id);
      }).catch((err) => {
        this.show_error(err?.response?.data?.message || "Failed to delete sub-programme.");
      });
    },
    redirect_to_sub_program_jobs(sp_id, sp_name, project_id) {
      sessionStorage.setItem("project_id", project_id);
      sessionStorage.setItem("sub_program_id", sp_id);
      sessionStorage.setItem("sub_program_name", sp_name);
      sessionStorage.setItem("sub_program_context", "1");
      this.$router.push({ path: "/manage_entity_type" });
    },
    format_date_short(val) {
      if (!val) return "-";
      return new Date(val).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
